import { useEffect, useMemo, useRef, useState } from "react";
import Seo from "@/components/base/Seo";
import EmptyState from "@/components/base/EmptyState";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import { useAuth } from "@/context/AuthContext";
import { deleteOwnerMedia, getOwnerMedia, getOwnerListings, uploadOwnerMedia } from "@/services/ownerApi";
import { uploadUrl } from "@/services/config";
import type { OwnedListing, OwnerMedia } from "@/types";

export default function OwnerMediaLibrary() {
  const { token } = useAuth();
  const [media, setMedia] = useState<OwnerMedia[]>([]);
  const [listings, setListings] = useState<OwnedListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [listingFilter, setListingFilter] = useState("all");
  const [uploadListing, setUploadListing] = useState("");
  const [urlInput, setUrlInput] = useState("");
  const [notice, setNotice] = useState("");
  const [uploading, setUploading] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<OwnerMedia | null>(null);
  const [deleting, setDeleting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    Promise.all([getOwnerMedia(token), getOwnerListings(token)])
      .then(([m, l]) => {
        if (!active) return;
        setMedia(m);
        setListings(l);
        setUploadListing(l[0]?.id ?? "");
      })
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token]);

  const filtered = useMemo(
    () => (listingFilter === "all" ? media : media.filter((m) => m.listingId === listingFilter)),
    [media, listingFilter],
  );

  const addMedia = (url: string, name: string, size: string) => {
    const listing = listings.find((l) => l.id === uploadListing);
    if (!url.trim()) return;
    const item: OwnerMedia = {
      id: `med-${Date.now()}`,
      url: url.trim(),
      name,
      listingId: uploadListing,
      listingName: listing?.name ?? "Unassigned",
      size,
      uploadedAt: new Date().toISOString().slice(0, 10),
    };
    setMedia((prev) => [item, ...prev]);
    setUrlInput("");
    setNotice("Photo added to your library.");
    setTimeout(() => setNotice(""), 2500);
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      // Uploads land in the server's /uploads folder and come back as a public
      // URL — nothing is stored in a browser temp/object URL.
      const url = await uploadOwnerMedia(file, token);
      addMedia(url, file.name, `${Math.round(file.size / 1024)} KB`);
    } catch (err) {
      setNotice(err instanceof Error ? err.message : "Upload failed. Please try again.");
      setTimeout(() => setNotice(""), 3500);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async () => {
    const target = pendingDelete;
    if (!target) return;
    setDeleting(true);
    const snapshot = media;
    setMedia((prev) => prev.filter((m) => m.id !== target.id));
    const ok = await deleteOwnerMedia(target.id, token);
    setDeleting(false);
    setPendingDelete(null);
    if (!ok) {
      setMedia(snapshot);
      setNotice("We couldn't remove that photo. Please try again.");
    } else {
      setNotice("Photo removed.");
    }
    setTimeout(() => setNotice(""), 2500);
  };

  return (
    <>
      <Seo title="Media Library" path="/dashboard/media" />

      <div>
        <h1 className="font-heading text-2xl font-extrabold text-foreground-950">Media library</h1>
        <p className="mt-1 text-sm text-foreground-600">Manage the photos that appear on your business listings.</p>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
        <div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-sm text-foreground-600">
              <span className="font-semibold text-foreground-950">{filtered.length}</span> photo{filtered.length === 1 ? "" : "s"}
            </span>
            <select
              value={listingFilter}
              onChange={(e) => setListingFilter(e.target.value)}
              className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none cursor-pointer"
            >
              <option value="all">All listings</option>
              {listings.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          </div>

          <div className="mt-4">
            {loading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="aspect-[4/3] animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
                ))}
              </div>
            ) : error ? (
              <EmptyState icon="ri-wifi-off-line" title="Couldn't load your media" description="Please refresh and try again." />
            ) : filtered.length === 0 ? (
              <EmptyState icon="ri-image-2-line" title="No photos yet" description="Add photos from the panel to bring your listings to life." />
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {filtered.map((m) => (
                  <figure key={m.id} className="group overflow-hidden rounded-lg border border-background-200 bg-background-50">
                    <div className="relative aspect-[4/3] w-full overflow-hidden">
                      <img src={uploadUrl(m.url)} alt={m.name} className="h-full w-full object-cover" />
                      <button
                        type="button"
                        onClick={() => setPendingDelete(m)}
                        aria-label={`Remove ${m.name}`}
                        className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-md bg-background-50/90 text-secondary-700 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-background-50 cursor-pointer"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                    <figcaption className="p-3">
                      <p className="truncate text-xs font-semibold text-foreground-900">{m.name}</p>
                      <p className="mt-0.5 truncate text-[11px] text-foreground-500">{m.listingName}</p>
                      <p className="mt-0.5 text-[11px] text-foreground-400">{m.size} · {m.uploadedAt}</p>
                    </figcaption>
                  </figure>
                ))}
              </div>
            )}
          </div>
        </div>

        <aside className="space-y-4">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-sm font-bold text-foreground-950">Add a photo</h2>
            <label htmlFor="uploadListing" className="mt-4 mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500">
              Attach to listing
            </label>
            <select
              id="uploadListing"
              value={uploadListing}
              onChange={(e) => setUploadListing(e.target.value)}
              className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:border-primary-400 focus:outline-none cursor-pointer"
            >
              {listings.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>

            <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-background-400 px-4 py-6 text-sm font-semibold text-foreground-700 hover:border-primary-400 hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
            >
              {uploading ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-xl"></i> Uploading…
                </>
              ) : (
                <>
                  <i className="ri-upload-cloud-2-line text-xl"></i> Upload from device
                </>
              )}
            </button>

            <div className="mt-4">
              <label htmlFor="mediaUrl" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500">
                Or paste an image URL
              </label>
              <input
                id="mediaUrl"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="https://…"
                className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:border-primary-400 focus:outline-none"
              />
              <button
                type="button"
                onClick={() => addMedia(urlInput, "pasted-image.jpg", "—")}
                disabled={!urlInput.trim()}
                className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-add-line"></i> Add to library
              </button>
            </div>

            {notice && (
              <div className="mt-4 flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 p-3 text-xs text-accent-900">
                <i className="ri-checkbox-circle-line text-accent-600"></i> {notice}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-background-200 bg-background-100/60 p-5">
            <h2 className="font-heading text-sm font-bold text-foreground-950">Photo tips</h2>
            <ul className="mt-3 space-y-2.5 text-xs text-foreground-600">
              <li className="flex gap-2"><i className="ri-check-line text-accent-600"></i> Use a bright, well-framed cover photo (1200×800 px).</li>
              <li className="flex gap-2"><i className="ri-check-line text-accent-600"></i> Show real products, premises and staff.</li>
              <li className="flex gap-2"><i className="ri-check-line text-accent-600"></i> Keep a consistent warm, natural style across photos.</li>
            </ul>
          </div>
        </aside>
      </div>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Remove this photo?"
        message={pendingDelete ? `“${pendingDelete.name}” will be deleted from your library. This cannot be undone.` : ""}
        confirmLabel="Remove photo"
        busy={deleting}
        onConfirm={remove}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
}